import { useEffect, useState } from "react";
import type { ThreadMessageLike } from "@assistant-ui/react";
import { ConversationStatus } from "~/components/assistant/types";
import { fetchConnect } from "./agui-messages";

/**
 * The shared AG-UI state shape returned by the /connect endpoint.
 *
 * Contains artifacts from all prior turns (keyed by message ID) and a
 * null context_buffer placeholder. This is the initial state that
 * StateDeltaEvent patches are applied against during a run.
 */
export interface AgUiState {
  artifacts: Record<string, string[]>;
  context_buffer: null | {
    used_tokens: number;
    free_tokens: number;
  };
  enable_pipeline_tools: boolean;
}

interface UseAgUiStateReturn {
  initialState: AgUiState | null;
  /** Pre-converted history from /connect so useHistoryLoad can seed the thread instantly on re-visit. */
  initialMessages: ThreadMessageLike[] | null;
  /**
   * Conversation status at connect time. `MESSAGE_IN_PROGRESS` means a
   * run is live server-side and the client should attach to its replay
   * stream.
   */
  status: ConversationStatus;
  /**
   * UUID of the agent bound to this conversation, surfaced by /connect.
   * `null` before the fetch resolves and on legacy conversations that
   * have not been migrated to an explicit agent_id yet.
   */
  agentId: string | null;
}

/**
 * Fetches the AG-UI connect payload on mount.
 *
 * Returns both the state (passed as `initialState` to the HttpAgent) and
 * the converted message history so the persistence layer can skip the
 * separate round-trip.
 *
 * Each ConversationRuntime instance is keyed on conversationId, so this
 * hook only ever fetches once per mount.
 */
export function useAgUiState(baseUrl: string, conversationId: string | null): UseAgUiStateReturn {
  const [initialState, setInitialState] = useState<AgUiState | null>(null);
  const [initialMessages, setInitialMessages] = useState<ThreadMessageLike[] | null>(null);
  const [status, setStatus] = useState<ConversationStatus>(ConversationStatus.READY);
  const [agentId, setAgentId] = useState<string | null>(null);

  useEffect(() => {
    if (!conversationId) {
      setInitialState({
        artifacts: {},
        context_buffer: null,
        enable_pipeline_tools: false,
      });
      setInitialMessages([]);
      setStatus(ConversationStatus.READY);
      setAgentId(null);
      return;
    }

    let cancelled = false;

    fetchConnect<AgUiState>(baseUrl, conversationId)
      .then((payload) => {
        if (cancelled) return;
        if (payload) {
          setInitialState(payload.state);
          setInitialMessages(payload.messages);
          setStatus(payload.status);
          setAgentId(payload.agentId);
        } else {
          // 400: fresh conversation with no committed history yet.
          setInitialState({
            artifacts: {},
            context_buffer: null,
            enable_pipeline_tools: false,
          });
          setInitialMessages([]);
          setStatus(ConversationStatus.READY);
          setAgentId(null);
        }
      })
      .catch((error) => {
        console.error("[AG-UI] connect failed, using empty state:", error);
        if (!cancelled) {
          setInitialState({
            artifacts: {},
            context_buffer: null,
            enable_pipeline_tools: false,
          });
          setInitialMessages(null);
          setStatus(ConversationStatus.READY);
          setAgentId(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [baseUrl, conversationId]);

  return { initialState, initialMessages, status, agentId };
}
